/*
 * Developed for G.J. Gardner Homes by Softeq Development Corporation
 * http://www.softeq.com
 */

import { ChangeDetectionStrategy, Component, ElementRef, HostListener, Input, OnInit, Renderer2 } from '@angular/core';
import { severityToString } from '@gh/core-messages';
import { TranslationService } from '@gh/core-mls';
import { isDetailedMessage, MOUSE_MIDDLE_BUTTON } from '@gh/core-ui';
import { stopEvent } from '@gh/core-util';
import { Notification, NotificationStatus } from './notification';

/**
 * Component for one notification item
 */
@Component({
  selector: 'gh-notification-item',
  template: require('./notification-item.component.html'),
  styles: [require('./notification-item.component.scss')],
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class NotificationItemComponent implements OnInit {
  @Input() item: Notification;

  constructor(private elementRef: ElementRef,
              private renderer: Renderer2,
              private translationService: TranslationService) {

  }

  get title(): string {
    return this.translationService.get(`lbl_notification_title_${severityToString(this.item.config.severity)}`);
  }

  get text(): string {
    const raw = this.translationService.getRaw(this.item.config.messageId);

    if (isDetailedMessage(raw)) {
      return this.translationService.get(this.item.config.messageId + '.title', this.item.config.messageParams);
    } else {
      return this.translationService.get(this.item.config.messageId, this.item.config.messageParams);
    }
  }

  ngOnInit(): void {
    this.renderer.addClass(this.elementRef.nativeElement, `severity-${severityToString(this.item.config.severity)}`);

    this.item.status.subscribe((status) => {
      if (status === NotificationStatus.Hiding) {
        this.renderer.addClass(this.elementRef.nativeElement, 'hiding');
      } else {
        this.renderer.removeClass(this.elementRef.nativeElement, 'hiding');
      }
    });
  }

  @HostListener('click', ['$event'])
  onClick(event: MouseEvent): void {
    this.item.touch();
    this.item.clicks.next(void 0);
  }

  @HostListener('mousedown', ['$event'])
  onMouseDown(event: MouseEvent): void {
    // prevent displaying of scroll handle when user clicks middle mouse button
    if (event.button === MOUSE_MIDDLE_BUTTON) {
      stopEvent(event);
    }
  }

  @HostListener('mouseup', ['$event'])
  onMouseUp(event: MouseEvent): void {
    // close notification when user clicks middle mouse button
    if (event.button === MOUSE_MIDDLE_BUTTON) {
      this.item.close();
    }
  }

  @HostListener('mouseenter')
  onEnter(): void {
    this.item.block();
  }

  @HostListener('mouseleave')
  onLeave(): void {
    this.item.free();
  }

  onClose(event: MouseEvent): void {
    stopEvent(event);

    this.item.close();
  }
}
